import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Car, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const VINDecoder = ({ onVehicleDecoded, showFullInfo = true, compact = false }) => {
  const { t, i18n } = useTranslation();
  const [vin, setVin] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleDecode = async () => {
    if (!vin || vin.length !== 17) {
      setError(t('vin.error_invalid_length'));
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/vin/decode`, { vin });
      
      if (response.data.success) {
        setResult(response.data);
        if (onVehicleDecoded) {
          onVehicleDecoded(response.data.vehicle);
        }
      } else {
        setError(response.data.message || t('common.error'));
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleDecode();
    }
  };

  if (compact) {
    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder={t('vin.enter_vin')}
            value={vin}
            onChange={(e) => setVin(e.target.value.toUpperCase())}
            onKeyPress={handleKeyPress}
            maxLength={17}
            className="flex-1"
            dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
          />
          <Button onClick={handleDecode} disabled={loading || vin.length !== 17}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('vin.decode_button')}
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {result && result.success && (
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>
              <strong>{result.vehicle.year} {result.vehicle.make} {result.vehicle.model}</strong>
              {result.vehicle.trim && ` - ${result.vehicle.trim}`}
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Car className="h-5 w-5" />
          {t('vin.decoder')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="vin">{t('vin.vin_label')}</Label>
          <div className="flex gap-2">
            <Input
              id="vin"
              type="text"
              placeholder={`${t('vin.enter_vin')} (${t('vin.vin_placeholder')})`}
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              maxLength={17}
              className="flex-1 font-mono"
              dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
            />
            <Button 
              onClick={handleDecode} 
              disabled={loading || vin.length !== 17}
              className="min-w-[120px]"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('vin.decoding')}
                </>
              ) : (
                t('vin.decode_button')
              )}
            </Button>
          </div>
          <p className="text-sm text-gray-500">
            {t('vin.instant_lookup')}
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {result && result.success && showFullInfo && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription>
              <div className="space-y-3">
                <div>
                  <h4 className="font-bold text-lg text-green-800 mb-2">
                    {result.vehicle.year} {result.vehicle.make} {result.vehicle.model}
                  </h4>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {result.vehicle.trim && (
                    <div>
                      <span className="font-semibold">{t('vin.labels.trim')}:</span> {result.vehicle.trim}
                    </div>
                  )}
                  {result.vehicle.body_class && (
                    <div>
                      <span className="font-semibold">{t('vin.labels.body_type')}:</span> {result.vehicle.body_class}
                    </div>
                  )}
                  {result.vehicle.engine_cylinders && (
                    <div>
                      <span className="font-semibold">{t('vin.labels.engine')}:</span> {result.vehicle.engine_cylinders} {t('common.info')} 
                      {result.vehicle.engine_displacement_l && ` (${result.vehicle.engine_displacement_l}L)`}
                    </div>
                  )}
                  {result.vehicle.fuel_type && (
                    <div>
                      <span className="font-semibold">{t('vin.labels.fuel_type')}:</span> {result.vehicle.fuel_type}
                    </div>
                  )}
                  {result.vehicle.transmission && (
                    <div>
                      <span className="font-semibold">{t('vin.labels.transmission')}:</span> {result.vehicle.transmission}
                    </div>
                  )}
                  {result.vehicle.drive_type && (
                    <div>
                      <span className="font-semibold">{t('vin.labels.drive')}:</span> {result.vehicle.drive_type}
                    </div>
                  )}
                  {result.vehicle.doors && (
                    <div>
                      <span className="font-semibold">{t('vin.labels.doors')}:</span> {result.vehicle.doors}
                    </div>
                  )}
                  {result.vehicle.manufacturer && (
                    <div className="col-span-2">
                      <span className="font-semibold">{t('vin.labels.manufacturer')}:</span> {result.vehicle.manufacturer}
                    </div>
                  )}
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default VINDecoder;
